// 音声セッション（Gemini Live）の結線。
//
// 役割: マイク入力 → Gemini Live → 音声再生の往復を組み立て、Gemini からの UI 操作
//       （fill_prompt / capture_map）をアプリ側の実装へ繋ぐ。Gemini に渡すのは UI 操作の
//       2 関数だけで、DuckDB などアプリのツールは一切渡さない（空間処理は Claude の担当）。
// 注入: { apiKey, model, layers, dataTables, isAgentRunning, setChatInput, chatInputRef,
//         log, onFillPrompt }。地図キャンバスの取得は utils/capture-map へ委譲する。
// 備考: @google/genai は初回接続時に動的 import する（初期バンドルを太らせないため）。
import { useCallback, useEffect, useRef, useState } from 'react'

import { captureMapImage } from '../utils/capture-map'
import { startAudioCapture } from '../voice/audio-capture'
import { createAudioPlayer } from '../voice/audio-player'
import { buildContextSnapshot, buildVoiceInstruction } from '../voice/voice-instruction'
import { CAPTURE_MAP, FILL_PROMPT, dispatchToolCall } from '../voice/voice-tools'

export function useVoiceSession({
  apiKey,
  model,
  layers = [],
  dataTables = [],
  isAgentRunning = false,
  setChatInput,
  chatInputRef,
  log,
  onFillPrompt,
} = {}) {
  // idle → connecting → listening ⇄ speaking / error
  const [voiceState, setVoiceState] = useState('idle')
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState('')
  const [elapsed, setElapsed] = useState(0)

  const sessionRef = useRef(null)
  const captureRef = useRef(null)
  const playerRef = useRef(null)
  // コールバックから最新値を読むための参照（クロージャの陳腐化を避ける）。
  const contextRef = useRef({ layers, dataTables, isAgentRunning })
  const stoppingRef = useRef(false)

  useEffect(() => {
    contextRef.current = { layers, dataTables, isAgentRunning }
  }, [layers, dataTables, isAgentRunning])

  // 通話中だけ経過時間を数える（コストが見えるように）。
  useEffect(() => {
    if (voiceState !== 'listening' && voiceState !== 'speaking') return undefined
    const timer = setInterval(() => setElapsed((v) => v + 1), 1000)
    return () => clearInterval(timer)
  }, [voiceState])

  // セッションの後片付け。理由を渡すとログに出す。
  const teardown = useCallback(
    async (reason = '') => {
      if (stoppingRef.current) return
      stoppingRef.current = true
      try {
        const capture = captureRef.current
        captureRef.current = null
        if (capture) {
          await capture.stop()
          // サーバー側の音声バッファを確定させてから閉じる。
          sessionRef.current?.sendAudioStreamEnd?.()
        }
        sessionRef.current?.close?.()
        sessionRef.current = null
        await playerRef.current?.close?.()
        playerRef.current = null
      } finally {
        stoppingRef.current = false
        setVoiceState((s) => (s === 'error' ? s : 'idle'))
        if (reason) log?.(`🎙 音声セッション終了: ${reason}`)
      }
    },
    [log],
  )

  const fail = useCallback(
    (message) => {
      setError(message)
      setVoiceState('error')
      log?.(`✗ 音声: ${message}`)
      teardown()
    },
    [log, teardown],
  )

  // Gemini からの関数呼び出しをアプリ側の実装へ振り分ける。
  // 応答には毎回そのときのレイヤー状況を同梱し、会話中の変化を伝える。
  const handleToolCall = useCallback(
    async (toolCall) => {
      const snapshot = () => buildContextSnapshot(contextRef.current)
      const responses = await dispatchToolCall(toolCall, {
        [FILL_PROMPT]: async ({ text }) => {
          const content = text.trim()
          if (!content) throw new Error('text が空です')
          setChatInput?.(content)
          onFillPrompt?.(content)
          // 既存の @ ボタン（App.handleMentionLayer）と同じく、書き込んだら入力欄へフォーカスする。
          window.requestAnimationFrame(() => chatInputRef?.current?.focus())
          log?.(`✓ 音声: fill_prompt（${content.length} 文字）`)
          return {
            ok: true,
            filled_chars: content.length,
            note: '入力欄に書き込みました。送信ボタンを押すのはユーザーです。勝手に実行されることはありません。',
            ...snapshot(),
          }
        },
        [CAPTURE_MAP]: async ({ reason } = {}) => {
          log?.(`▶ 音声: capture_map${reason ? `（${reason}）` : ''}`)
          const image = await captureMapImage()
          // 先に画像を realtime input として流し、その後でツール応答を返す。
          // ツール応答がモデルのターンを再開させるため、順序を逆にすると画像を見ないまま話し始める。
          sessionRef.current?.sendImage(image.base64, image.mimeType)
          log?.(`✓ 音声: capture_map（${image.width}×${image.height}）`)
          return {
            ok: true,
            note: '直前に送った画像が現在の地図です。読み込み中で白い場合は推測せずユーザーに尋ねてください。',
            ...snapshot(),
          }
        },
      })
      const failed = responses.find((r) => r.response?.ok === false)
      if (failed) log?.(`✗ 音声: ${failed.name}: ${failed.response.error}`)
      sessionRef.current?.sendToolResponses(responses)
    },
    [chatInputRef, log, onFillPrompt, setChatInput],
  )

  const start = useCallback(async () => {
    if (sessionRef.current || voiceState === 'connecting') return
    if (!apiKey) {
      fail('Gemini APIキーが設定されていません。⚙ API設定 から入力してください。')
      return
    }
    setError('')
    setTranscript('')
    setElapsed(0)
    setVoiceState('connecting')
    try {
      const { layers: currentLayers, dataTables: currentTables } = contextRef.current

      const player = createAudioPlayer({
        onStateChange: (playing) => {
          // 再生中は「応答中」、終わったら「聞き取り中」に戻す。
          setVoiceState((s) =>
            s === 'idle' || s === 'error' ? s : playing ? 'speaking' : 'listening',
          )
        },
      })
      playerRef.current = player

      // 初回接続時にだけ SDK を読み込む。
      const { connectGeminiLive } = await import('../voice/gemini-live-client')
      const session = await connectGeminiLive({
        apiKey,
        model,
        systemInstruction: buildVoiceInstruction({
          layers: currentLayers,
          dataTables: currentTables,
        }),
        callbacks: {
          onAudio: (base64) => player.enqueue(base64),
          onOutputTranscript: (text) => setTranscript((prev) => `${prev}${text}`),
          onTurnComplete: () => setTranscript((prev) => prev.trim()),
          onInterrupted: () => {
            player.flush()
            setTranscript('')
          },
          onToolCall: (toolCall) => {
            handleToolCall(toolCall).catch((e) => log?.(`✗ 音声ツール: ${String(e?.message ?? e)}`))
          },
          onGoAway: (timeLeft) => log?.(`音声セッションはまもなく終了します（残り ${timeLeft}）`),
          onError: (e) => fail(String(e?.message ?? e)),
          onClose: (reason) => teardown(reason || 'サーバーから切断されました'),
        },
      })
      sessionRef.current = session

      const capture = await startAudioCapture({
        onChunk: (base64) => sessionRef.current?.sendAudio(base64, captureRef.current?.sampleRate),
      })
      captureRef.current = capture
      setVoiceState('listening')
      log?.(`🎙 音声セッション開始（${model}）`)
    } catch (e) {
      const message = String(e?.message ?? e)
      fail(
        /NotAllowedError|Permission/.test(message)
          ? 'マイクの使用が許可されませんでした。ブラウザの権限設定を確認してください。'
          : message,
      )
    }
  }, [apiKey, fail, handleToolCall, log, model, teardown, voiceState])

  const stop = useCallback(() => {
    setTranscript('')
    teardown('ユーザー操作で停止')
  }, [teardown])

  // アンマウント時にマイクと WebSocket を必ず解放する。
  useEffect(() => () => void teardown(), [teardown])

  return { voiceState, transcript, error, elapsed, start, stop }
}
